import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'

import { apiClient } from '@/api/client'
import { useAuthStore } from '@/stores/auth-store'

import type {
  AuthError,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
} from './types'

function getAuthErrorMessage(error: unknown): string {
  if (isAxiosError<AuthError>(error)) {
    return error.response?.data?.error?.message ?? 'Something went wrong'
  }
  return 'Something went wrong'
}

function getAuthErrorCode(error: unknown): string | null {
  if (isAxiosError<AuthError>(error)) {
    return error.response?.data?.error?.code ?? null
  }
  return null
}

export function useLoginMutation() {
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const response = await apiClient.post<AuthResponse>('/auth/login', data)
      return response.data
    },
    onSuccess: (data) => {
      setAuth(data.user, data.access_token)
      void navigate({ to: '/dashboard' })
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useRegisterMutation() {
  const { setAuth } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: RegisterRequest) => {
      const response = await apiClient.post<AuthResponse>(
        '/auth/register',
        data,
      )
      return response.data
    },
    onSuccess: (data) => {
      setAuth(data.user, data.access_token)
      void navigate({ to: '/create' })
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useLogoutMutation() {
  const { clearAuth } = useAuthStore()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/logout')
    },
    onSuccess: () => {
      clearAuth()
      void navigate({ to: '/login' })
    },
    onError: () => {
      // Clear auth even if logout request fails
      clearAuth()
      void navigate({ to: '/login' })
    },
  })
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: async (data: { email: string }) => {
      await apiClient.post('/auth/forgot-password', data)
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: async (data: { token: string; new_password: string }) => {
      await apiClient.post('/auth/reset-password', data)
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useVerifyEmailMutation() {
  return useMutation({
    mutationFn: async (data: { token: string }) => {
      await apiClient.post('/auth/verify-email', data)
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useResendVerificationMutation() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/resend-verification')
    },
    meta: { skipGlobalErrorToast: true },
  })
}

export function useUpdateProfileMutation() {
  const { setUser } = useAuthStore()

  return useMutation({
    mutationFn: async (data: { display_name: string }) => {
      const response = await apiClient.patch<User>('/auth/me', data)
      return response.data
    },
    onSuccess: (user) => {
      setUser(user)
    },
  })
}

export function useRefreshMutation() {
  const { setAuth, clearAuth } = useAuthStore()

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<AuthResponse>('/auth/refresh')
      return response.data
    },
    onSuccess: (data) => {
      setAuth(data.user, data.access_token)
    },
    onError: () => {
      clearAuth()
    },
    meta: { skipGlobalErrorToast: true },
  })
}

function getGoogleLoginUrl(): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1'
  return `${baseUrl}/auth/google`
}

export function redirectToGoogleLogin() {
  window.location.href = getGoogleLoginUrl()
}

export { getAuthErrorMessage, getAuthErrorCode }
